import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuCircleHelp, LuChevronDown, LuChevronUp, LuLightbulb, LuX } from "react-icons/lu";
import HELP_CONTENT from "../helpContent";

/**
 * Contextual page-help trigger — CareerSavvy's take on ElevateStaffing's "?"
 * PageHelper. Renders a small "?" button (meant to sit next to a page title);
 * clicking it opens a popover with a short tip, an expandable feature list and
 * a pro tip. No-ops silently when there's no help entry for the slug.
 *
 * The popover is portalled to <body> so the topbar's overflow can't clip it; it
 * is wrapped in `.csx` so the design tokens (and light/dark theme) resolve, and
 * because the app's zoom lives on `.csx-app`/`.csx-page` (not bare `.csx`) the
 * popover is not double-scaled.
 */
export default function InfoHelper({ slug }) {
  const data = slug ? HELP_CONTENT[slug] : null;
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const cardRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setExpanded(false);
  }, []);

  const calcPos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 300;
    let top = r.bottom + 8;
    let left = r.left;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
    if (window.innerHeight - r.bottom < 320) top = Math.max(8, r.top - 328);
    setPos({ top, left: Math.max(8, left) });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        close();
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    const onReflow = () => calcPos();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, close, calcPos]);

  if (!data) return null;

  const features = (data.features || []).map((f) => {
    if (typeof f === "string") {
      const p = f.split("**");
      return { label: p[1] || "", description: (p[2] || "").replace(/^\s*[—–-]\s*/, "") };
    }
    return f;
  });

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (open) close();
    else {
      calcPos();
      setOpen(true);
    }
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="cs-help-trigger"
        onClick={toggle}
        aria-label={`About ${data.title}`}
        title={`About ${data.title}`}
      >
        <LuCircleHelp size={14} />
      </button>

      {open &&
        createPortal(
          <div className="csx">
            <div ref={cardRef} className="cs-help-pop" style={{ top: pos.top, left: pos.left }} onClick={(e) => e.stopPropagation()}>
              <div className="cs-help-head">
                <span className="cs-help-title">{data.title}</span>
                <button type="button" className="cs-help-x" onClick={close} aria-label="Close">
                  <LuX size={14} />
                </button>
              </div>

              <div className="cs-help-body">
                <p className="cs-help-tip">{data.shortTip}</p>

                {(features.length > 0 || data.proTip) && (
                  <button type="button" className="cs-help-more" onClick={() => setExpanded((v) => !v)}>
                    {expanded ? "Show less" : "Learn more"}
                    {expanded ? <LuChevronUp size={12} /> : <LuChevronDown size={12} />}
                  </button>
                )}

                {expanded && (
                  <div className="cs-help-expand">
                    {features.length > 0 && (
                      <ul className="cs-help-list">
                        {features.map((f, i) => (
                          <li key={i}>
                            <span className="cs-help-dot" />
                            <span>
                              <span className="cs-help-flabel">{f.label}</span>
                              {f.description ? <span className="cs-help-fdesc"> — {f.description}</span> : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {data.proTip && (
                      <div className="cs-help-protip">
                        <LuLightbulb size={14} className="cs-help-protip-icon" />
                        <p>
                          <strong>Pro tip: </strong>
                          {data.proTip}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
